import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { resolveRecentCodeProjects, touchRecentCodeProject } from "../../config/codeProjects";
import { categoryPathLabel } from "../../utils/textRoutes";
import { toSortedArray } from "../../services/workspaceService";

const HOY_CATEGORIES = ["código", "iglesia", "vida", "cuentos"];
const ARROW_W = 36;
const CHIP_GAP = 6;
const CHIP_MIN = 52;

function categoryLabel(cat) {
  return cat === "código" ? "Código" : cat.charAt(0).toUpperCase() + cat.slice(1);
}

export default function HoyCategoryBar({
  workspace,
  settings,
  categoryPaths,
  activeCategory,
  setActiveCategory,
  activeCodeProject,
  setActiveCodeProject,
  onEditProjects,
  viewToggleStyle,
  viewToggleActiveStyle,
}) {
  const barRef = useRef(null);
  const [width, setWidth] = useState(320);
  const [codeOffset, setCodeOffset] = useState(0);

  const recentProjects = useMemo(
    () => resolveRecentCodeProjects(settings),
    [settings],
  );

  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width || 320);
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width || 320);
    return () => ro.disconnect();
  }, []);

  const maxChips = useMemo(() => {
    const usable = Math.max(0, width - ARROW_W * 2 - CHIP_GAP * 2);
    return Math.max(1, Math.floor(usable / (CHIP_MIN + CHIP_GAP)));
  }, [width]);

  const pickProject = useCallback(
    (id) => {
      if (id) touchRecentCodeProject(id);
      setActiveCodeProject(id);
      setActiveCategory("código");
    },
    [setActiveCategory, setActiveCodeProject],
  );

  const codeItems = useMemo(
    () => [{ id: null, label: "Notas", color: null }, ...recentProjects.map((p) => p)],
    [recentProjects],
  );

  const codeWindow = useMemo(() => {
    const len = codeItems.length;
    const start = len <= maxChips ? 0 : Math.min(codeOffset, len - maxChips);
    return { start, items: codeItems.slice(start, start + maxChips), len, canPrev: start > 0, canNext: start + maxChips < len };
  }, [codeItems, codeOffset, maxChips]);

  useEffect(() => {
    setCodeOffset(0);
  }, [activeCategory, recentProjects.length]);

  const visibleCategories = useMemo(() => {
    if (HOY_CATEGORIES.length <= maxChips) return HOY_CATEGORIES;
    const idx = activeCategory ? HOY_CATEGORIES.indexOf(activeCategory) : 0;
    const start = Math.max(0, Math.min(idx, HOY_CATEGORIES.length - maxChips));
    return HOY_CATEGORIES.slice(start, start + maxChips);
  }, [activeCategory, maxChips]);

  const chipBtn = (active, extra = {}) => ({
    ...viewToggleStyle,
    ...(active ? viewToggleActiveStyle : {}),
    fontSize: "0.75rem",
    padding: "6px 10px",
    flex: "0 1 auto",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...extra,
  });

  const arrowBtn = {
    ...viewToggleStyle,
    padding: "6px 8px",
    flex: "0 0 auto",
    width: ARROW_W,
    minWidth: ARROW_W,
  };

  if (activeCategory === "código") {
    return (
      <div ref={barRef} className="hoy-category-bar" role="group" aria-label="Proyectos de código">
        <button
          type="button"
          style={arrowBtn}
          aria-label="Volver a categorías"
          onClick={() => {
            setActiveCategory(null);
            setActiveCodeProject(null);
            setCodeOffset(0);
          }}
        >
          <ChevronLeft size={16} />
        </button>
        {codeWindow.canPrev && (
          <button type="button" style={arrowBtn} aria-label="Proyectos anteriores" onClick={() => setCodeOffset((o) => Math.max(0, o - 1))}>
            <ChevronLeft size={14} />
          </button>
        )}
        {codeWindow.items.map((item) => {
          const id = item.id ?? null;
          const active = (id ?? null) === (activeCodeProject ?? null);
          return (
            <button
              key={id ?? "notas"}
              type="button"
              style={chipBtn(active, id ? { borderColor: active ? item.color : undefined } : {})}
              onClick={() => pickProject(id)}
              title={id ? `Temas › Código › ${item.notebookName || item.label}` : "Temas › Código › Notas"}
            >
              {item.label || item.notebookName || "Notas"}
            </button>
          );
        })}
        {codeWindow.canNext && (
          <button
            type="button"
            style={arrowBtn}
            aria-label="Más proyectos"
            onClick={() => setCodeOffset((o) => Math.min(codeWindow.len - maxChips, o + 1))}
          >
            <ChevronRight size={14} />
          </button>
        )}
        {width > 280 && (
          <button type="button" style={arrowBtn} onClick={onEditProjects} aria-label="Editar proyectos" title="Editar proyectos">
            <SlidersHorizontal size={14} />
          </button>
        )}
      </div>
    );
  }

  if (activeCategory && activeCategory !== "código") {
    return (
      <div ref={barRef} className="hoy-category-bar" role="group" aria-label="Carpeta de destino">
        <button type="button" style={arrowBtn} aria-label="Volver a categorías" onClick={() => setActiveCategory(null)}>
          <ChevronLeft size={16} />
        </button>
        <button type="button" style={chipBtn(true)} onClick={() => setActiveCategory(null)}>
          {categoryLabel(activeCategory)}
        </button>
      </div>
    );
  }

  return (
    <div ref={barRef} className="hoy-category-bar" role="group" aria-label="Carpeta de destino">
      {visibleCategories.map((cat) => (
        <button
          key={cat}
          type="button"
          style={chipBtn(false)}
          onClick={() => {
            if (cat === "código") {
              setActiveCategory("código");
              return;
            }
            setActiveCategory((c) => (c === cat ? null : cat));
            setActiveCodeProject(null);
          }}
          title={`Guardar en ${categoryPathLabel(categoryPaths[cat], workspace, toSortedArray)}`}
        >
          {categoryLabel(cat)}
        </button>
      ))}
    </div>
  );
}
